import React from "react";
import { ArrowBack, Bookmark, BookmarkAdd, Edit as EditIcon, Keyboard, Person, Refresh } from "@mui/icons-material";
import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import { Link } from "react-router-dom";

interface SongHeaderProps {
  title: string;
  artist?: string;
  savedListsCount: number;
  isOwner?: boolean;
  onBack: () => void;
  onOpenShortcuts: () => void;
  onOpenSaveModal: () => void;
  onRescrape?: () => void;
  onEdit?: () => void;
}

export const SongHeader: React.FC<SongHeaderProps> = ({
  title,
  artist,
  savedListsCount,
  isOwner = false,
  onBack,
  onOpenShortcuts,
  onOpenSaveModal,
  onRescrape,
  onEdit,
}) => {
  return (
    <Box
      display="flex"
      alignItems={{ xs: "flex-start", sm: "center" }}
      justifyContent="space-between"
      flexDirection={{ xs: "column", sm: "row" }}
      gap={1}
      mb={1.5}
    >
      <Box display="flex" alignItems="center">
        <IconButton onClick={onBack} sx={{ mr: 0.5, p: { xs: 0.5, sm: 1 } }}>
          <ArrowBack fontSize="small" />
        </IconButton>
        <Box>
          <Typography
            variant="h5"
            fontWeight="bold"
            sx={{ fontSize: { xs: "1.15rem", sm: "1.6rem" }, lineHeight: 1.2 }}
          >
            {title}
          </Typography>
          {artist && (
            <Box
              component={Link}
              to={`/Artist?name=${encodeURIComponent(artist)}`}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                textDecoration: "none",
                color: "primary.main",
                mt: 0.25,
                "&:hover": {
                  textDecoration: "underline",
                  color: "primary.dark",
                },
              }}
            >
              <Person sx={{ fontSize: 15 }} />
              <Typography variant="body2" component="span" fontWeight="500">
                {artist}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Box display="flex" alignItems="center" gap={1} alignSelf={{ xs: "flex-end", sm: "center" }} flexWrap="wrap">
        {onEdit && (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<EditIcon sx={{ fontSize: 16 }} />}
            onClick={onEdit}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              px: 1.5,
              py: 0.5,
              fontWeight: 600,
              fontSize: "0.8rem",
            }}
          >
            {isOwner ? "Edit Song" : "Edit yourself"}
          </Button>
        )}

        <Tooltip title="Keyboard Shortcuts (?)">
          <IconButton
            size="small"
            onClick={onOpenShortcuts}
            sx={{
              color: "#64748b",
              bgcolor: "#f8fafc",
              border: "1px solid #e2e8f0",
              p: 0.6,
            }}
          >
            <Keyboard fontSize="small" />
          </IconButton>
        </Tooltip>

        {onRescrape && (
          <Tooltip title="Re-scrape from source">
            <IconButton
              size="small"
              onClick={onRescrape}
              sx={{
                color: "#64748b",
                bgcolor: "#f8fafc",
                border: "1px solid #e2e8f0",
                p: 0.6,
              }}
            >
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <Button
          variant={savedListsCount > 0 ? "outlined" : "contained"}
          color="primary"
          size="small"
          startIcon={savedListsCount > 0 ? <Bookmark /> : <BookmarkAdd />}
          onClick={onOpenSaveModal}
          sx={{
            textTransform: "none",
            borderRadius: 2,
            px: 1.75,
            py: 0.5,
            fontWeight: 600,
            fontSize: "0.8rem",
            background:
              savedListsCount > 0
                ? "transparent"
                : "linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)",
          }}
        >
          {savedListsCount > 0 ? "Saved" : "Save to List"}
        </Button>
      </Box>
    </Box>
  );
};
